import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Activity,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';
import {
  getTrackContactCreationRequest,
  clearTrackContactCreationState,
} from '../../store/trackContactCreation/trackContactCreation.action';
import {
  selectTrackContactCreations,
  selectTrackContactCreationLoading,
  selectTrackContactCreationError,
  selectTrackContactCreationPagination,
} from '../../store/trackContactCreation/trackContactCreation.selector';
import { ITrackContactCreation } from '../../types/trackContactCreation/trackContactCreation.types';

// Type for the activity log structure based on user's provided data
interface ActivityLog {
  _id: string;
  workspaceId?: string;
  email?: string;
  body?: {
    attributes?: {
      [key: string]: any;
      firstName?: string;
      lastName?: string;
    };
    identifier?: {
      email?: string;
    };
  };
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

const ActivityLogs: React.FC = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  // Redux state
  const trackContactCreations = useSelector(selectTrackContactCreations);
  const loading = useSelector(selectTrackContactCreationLoading);
  const error = useSelector(selectTrackContactCreationError);
  const pagination = useSelector(selectTrackContactCreationPagination);

  // Local state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Fetch data on mount with default values
  useEffect(() => {
    const currentPage = pagination?.page ?? 0;
    const currentLimit = pagination?.limit ?? 10;

    dispatch(
      getTrackContactCreationRequest({
        page: currentPage,
        limit: currentLimit,
      }) as any
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // Only fetch on mount

  // Show error toast
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: `Failed to load activity logs: ${error}`,
      });
      dispatch(clearTrackContactCreationState());
    }
  }, [error, showToast, dispatch]);

  // Toggle row expansion
  const toggleRow = (logId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  // Convert trackContactCreations to ActivityLog format
  const activityLogs: ActivityLog[] = useMemo(() => {
    return (trackContactCreations || []).map((item: ITrackContactCreation) => ({
      _id: item._id || '',
      workspaceId: item.workspaceId,
      email: item.email,
      body: item.body,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      ...item,
    }));
  }, [trackContactCreations]);

  // Use API pagination data from Redux
  const currentPage = pagination?.page ?? 0;
  const currentLimit = pagination?.limit ?? 10;
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.totalPages || 0;
  const paginatedLogs = activityLogs;

  // Handle page change - dispatch with new page, Redux will update when API responds
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      dispatch(
        getTrackContactCreationRequest({
          page: newPage,
          limit: currentLimit,
        }) as any
      );
    }
  };

  // Handle limit change - reset to page 0 when limit changes
  const handleLimitChange = (newLimit: number) => {
    dispatch(
      getTrackContactCreationRequest({
        page: 0,
        limit: newLimit,
      }) as any
    );
  };

  return (
    <div className="p-6 space-y-4 bg-background">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Activity Logs
          </h1>
          <p className="text-gray-600 mt-1">
            View and track all activities across your workspace
          </p>
        </div>
      </div>

      {/* Activity Logs Count */}
      {!loading && paginatedLogs.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {totalItems} {totalItems === 1 ? 'activity log' : 'activity logs'}
          </div>

          {/* Limit Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 whitespace-nowrap">
              Show:
            </span>
            <div className="relative">
              <select
                value={currentLimit.toString()}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary shadow-sm hover:shadow-md cursor-pointer"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
            <span className="text-sm text-gray-500 whitespace-nowrap">
              per page
            </span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <p className="text-gray-600">Loading activity logs...</p>
          </div>
        </div>
      )}

      {/* Activity Logs Table or Empty State */}
      {!loading && paginatedLogs.length === 0 ? (
        <div className="bg-primary-background rounded-xl border border-border shadow-sm">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="text-center">
              <Activity className="w-16 h-16 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary/80 mb-2">
                No activity logs yet
              </h3>
              <p className="text-primary/80 mb-8 max-w-md">
                Activity logs will appear here as activities are recorded in
                your workspace.
              </p>
            </div>
          </div>
        </div>
      ) : !loading && paginatedLogs.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-12">
                  {/* Expand/Collapse column */}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[250px]">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[180px]">
                  Created At
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[180px]">
                  Updated At
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedLogs.map((log) => {
                const isExpanded = expandedRows.has(log._id);
                return (
                  <React.Fragment key={log._id}>
                    <tr
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => toggleRow(log._id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap w-12">
                        <div className="text-muted-foreground">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap min-w-[250px]">
                        <div className="text-sm font-medium text-card-foreground">
                          Contact Creation API V2
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap min-w-[180px]">
                        <div className="text-sm text-card-foreground">
                          {log.createdAt
                            ? new Date(log.createdAt).toLocaleString()
                            : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap min-w-[180px]">
                        <div className="text-sm text-card-foreground">
                          {log.updatedAt
                            ? new Date(log.updatedAt).toLocaleString()
                            : '—'}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={4} className="p-4 bg-muted/50">
                          <div className="relative">
                            <pre className="text-sm text-success bg-foreground rounded-lg p-5 overflow-x-auto max-h-96 overflow-y-auto font-mono leading-relaxed m-0 text-primary-foreground">
                              <code>
                                {JSON.stringify(log.body || log, null, 2)}
                              </code>
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Pagination */}
      {!loading && paginatedLogs.length > 0 && (
        <div className="flex items-center justify-end">
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="small"
                disabled={currentPage === 0 || loading}
                onClick={() => handlePageChange(currentPage - 1)}
                className="flex items-center gap-1 px-3 py-1"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <span className="text-sm text-gray-500 px-3 py-1">
                Page {currentPage + 1} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="small"
                disabled={currentPage >= totalPages - 1 || loading}
                onClick={() => handlePageChange(currentPage + 1)}
                className="flex items-center gap-1 px-3 py-1"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
